<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref, watchEffect, watch } from 'vue'
import { useRoute } from 'vue-router'
import router from '@/router.ts'
import { isNumeric } from '@/utils/util.ts'
import { isNull } from 'lodash'
import { useUserStore } from '@/stores/user.store.ts'
import * as IProjects from '@/components/projects/interfaces.ts'
import { storeToRefs } from 'pinia'
import { MINIMAL_COMFORT_ADMIN_WIDTH_PX } from '@/config/config.ts'
import { useSubscription } from '@vue/apollo-composable'
import { useTranslations } from '@/composables/useTranslations.ts'
import { usePageTitle } from '@/composables/usePageTitle.ts'
import { ChatInterceptAiControlMutation } from '@/components/chat/graphql/mutations.graphql.ts'
import { useMutation } from '@vue/apollo-composable'
import type { StatusResponse } from '@/components/users/interfaces.ts'
import {
  GET_CHAT_MESSAGES_SUBSCRIPTION,
  GET_CUSTOMER_CONNECTION_STATUS_SUBSCRIPTION,
  GET_CUSTOMER_TYPING_STATUS_SUBSCRIPTION,
} from '@/components/chat/graphql/subscriptions.graphql.ts'
import type {
  ChatTrackingInfo,
  ChatWSMessage,
  CustomerConnectionStatus,
  CustomerContactData,
  CustomerTypingStatus,
} from '@/components/chat/interfaces/chat.interfaces.ts'
import ChatMessagesV2 from '@/components/chat/chatV2/ChatMessagesV2.vue'
import ChatListV2 from '@/components/chat/chatV2/ChatListV2.vue'
import ChatDetailsSection from '@/components/chat/chatV2/ChatDetailsSection.vue'

const route = useRoute()
const userStore = useUserStore()
const { projectSelected } = storeToRefs(userStore)
const { t } = useTranslations()

const projectId = computed(() => (projectSelected.value as IProjects.Project)?.id ?? null)
const chatListScrollableDivRef = ref<HTMLDivElement | null>(null)

const selectedChatId = ref<number | null>(
  isNumeric(route.params.chatIndex) ? Number(route.params.chatIndex) : null,
)
const hasSmallScreenResolution = ref<boolean>(window.innerWidth < MINIMAL_COMFORT_ADMIN_WIDTH_PX)
const refChatList = ref<{
  onNewMessagesReceived: (msgs: ChatWSMessage[]) => void
  updateCustomerConnectionStatusByChatKey: (chatKey: string, isOnline: boolean) => void
  updateCustomerTypingStatusByChatKey: (chatKey: string, isTyping: boolean) => void
}>()
const refRouterView = ref<{
  onNewMessagesReceived: (msgs: ChatWSMessage[]) => void
  trackingInfo: ChatTrackingInfo | null
  isAiControlIntercepted: boolean
}>({
  onNewMessagesReceived: () => {},
  trackingInfo: null,
  isAiControlIntercepted: false,
})

const contactInfo = ref<CustomerContactData>({
  contactPhones: null,
  contactEmails: null,
})

function onChatSelected({ contactPhones, contactEmails }: CustomerContactData) {
  contactInfo.value = { contactPhones, contactEmails }
}

function onWindowResized() {
  hasSmallScreenResolution.value = window.innerWidth < MINIMAL_COMFORT_ADMIN_WIDTH_PX
}

const {
  onError: onErrorAiInterceptControl,
  onDone: onDoneAiInterceptControl,
  mutate: onMutateAiInterceptControl,
} = useMutation<{ status: StatusResponse }>(ChatInterceptAiControlMutation)

function onStopAIButtonClicked() {
  onMutateAiInterceptControl({
    projectId: projectId.value,
    chatIndex: parseInt(route.params.chatIndex as string),
  })

  onDoneAiInterceptControl((result) => {
    console.log('ChatMessages | onStopAIButtonClicked | Done:', result)
  })

  onErrorAiInterceptControl((error) => {
    console.error('ChatMessages | onStopAIButtonClicked | Error:', error)
  })
}

watchEffect(async () => {
  await router.isReady()

  selectedChatId.value = isNumeric(route.params.chatIndex) ? Number(route.params.chatIndex) : null
})

onMounted(() => {
  window.addEventListener('resize', onWindowResized) // TODO: Later: Move to upper level

  const { result: newMessages, stop: stopChatMessagesSubscription } = useSubscription(
    GET_CHAT_MESSAGES_SUBSCRIPTION,
    null,
    {
      fetchPolicy: 'no-cache',
    },
  )

  const { result: customerConnectionStatus, stop: stopCustomerConnectionStatusSubscription } =
    useSubscription(GET_CUSTOMER_CONNECTION_STATUS_SUBSCRIPTION, null, {
      fetchPolicy: 'no-cache',
    })

  const { result: customerTypingStatus, stop: stopCustomerTypingStatusSubscription } =
    useSubscription(GET_CUSTOMER_TYPING_STATUS_SUBSCRIPTION, null, {
      fetchPolicy: 'no-cache',
    })

  const { stop: stopNewMessagesWatcher } = watch<{
    chatMessages: ChatWSMessage[]
  }>(newMessages, ({ chatMessages }) => {
    // GET_CUSTOMERS_CONNECTION_STATUS_SUBSCRIPTION
    refChatList.value?.onNewMessagesReceived(chatMessages)
    refRouterView.value?.onNewMessagesReceived(chatMessages)
  })

  const { stop: stopCustomerConnectionStatusWatcher } = watch<{
    customerConnectionStatus: CustomerConnectionStatus
  }>(customerConnectionStatus, ({ customerConnectionStatus: status }) => {
    refChatList.value?.updateCustomerConnectionStatusByChatKey(status.chatKey, status.isOnline)
  })

  const { stop: stopCustomerTypingStatusWatcher } = watch<{
    customerTypingStatus: CustomerTypingStatus
  }>(customerTypingStatus, ({ customerTypingStatus: status }) => {
    refChatList.value?.updateCustomerTypingStatusByChatKey(status.chatKey, true)
  })

  onUnmounted(() => {
    window.removeEventListener('resize', onWindowResized) // TODO: Move away
    // Stop subscriptions
    stopChatMessagesSubscription()
    stopCustomerTypingStatusSubscription()
    stopCustomerConnectionStatusSubscription()

    // Stop subscriptions watchers
    stopNewMessagesWatcher()
    stopCustomerTypingStatusWatcher()
    stopCustomerConnectionStatusWatcher()
  })
})

const title = computed<string>(() => {
  const projectName = projectSelected?.value?.title || ''
  const chatName = selectedChatId.value
    ? `${t('chats.page.chat')} #${selectedChatId.value}`
    : `${t('navigation.chats')}`
  return projectName ? `${projectName} | ${chatName}` : chatName
})

usePageTitle(title)
</script>

<template>
  <div
    class="flex flex-col h-full max-h-[1500px] min-sm:max-h-[calc(100vh-13rem)] max-w-7xl sm:flex-row gap-2 mt-4 rounded-lg justify-center mx-auto xl:text-sm text-xs"
  >
    <div class="bg-white rounded-lg p-2 min-w-[280px]" ref="chatListScrollableDivRef">
      <ChatListV2
        v-if="!isNull(projectId)"
        ref="refChatList"
        :projectId="projectId"
        @onChatSelected="onChatSelected"
      />
      <div v-else class="flex items-center justify-center h-full">
        {{ t('project.pleaseChooseProjectOrRefreshPage') }}
      </div>
    </div>
    <div class="flex flex-col sm:flex-col-reverse xl:flex-row gap-2 md:w-2/3">
      <ChatMessagesV2
        v-if="!isNull(selectedChatId) && !isNull(projectId)"
        ref="refRouterView"
        :chatIndex="selectedChatId"
        :projectId="projectId"
        @onStopAiClicked="onStopAIButtonClicked"
      />
      <div v-else class="flex items-center justify-center h-full w-full bg-white rounded-lg p-2">
        <!--<div class="text-xs text-black md:text-sm font-semibold text-center">
          {{ t('chats.page.chooseChatFirst') }}
        </div>-->
      </div>

      <ChatDetailsSection
        class="min-w-42 xl:max-w-[300px] xl:flex-shrink-0"
        @onStopAiClicked="onStopAIButtonClicked"
        :info="refRouterView?.trackingInfo"
        :contactPhones="contactInfo.contactPhones"
        :contactEmails="contactInfo.contactEmails"
        :isAiControlIntercepted="refRouterView?.isAiControlIntercepted"
      />
    </div>
  </div>
  <div class="pre-footer-text">
    {{ t('project.chatsOverviewDescription') }}
  </div>
</template>
